import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, auth, photoUrl, FREQUENCY_LABEL, WASTE_LABEL, VISIT_PREFERENCE_LABEL, SERVICES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import StatusBadge from "../components/StatusBadge";
import { SignOut, Plus, Trash, PaperPlaneRight, CalendarBlank, XCircle, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [readySent, setReadySent] = useState([]);
  const [accepted, setAccepted] = useState([]);
  const [datesOffered, setDatesOffered] = useState([]);
  const [booked, setBooked] = useState([]);
  const [declined, setDeclined] = useState([]);
  const me = auth.getUser();

  useEffect(() => {
    if (!auth.getToken()) { nav("/admin/login"); return; }
    refreshAll();
    // eslint-disable-next-line
  }, []);

  const refreshAll = async () => {
    try {
      const [p, qr, a, dOff, b, d] = await Promise.all([
        api.get("/admin/quotes?status=pending"),
        api.get("/admin/quotes?status=quote_ready"),
        api.get("/admin/quotes?status=accepted"),
        api.get("/admin/quotes?status=dates_offered"),
        api.get("/admin/quotes?status=booked"),
        api.get("/admin/quotes?status=declined"),
      ]);
      setPending(p.data); setReadySent(qr.data); setAccepted(a.data); setDatesOffered(dOff.data); setBooked(b.data); setDeclined(d.data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        auth.clear(); nav("/admin/login");
      }
    }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Permanently delete the quote from ${q.name}? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/quotes/${q.id}`);
      toast.success("Quote deleted.");
      refreshAll();
    } catch { toast.error("Couldn't delete that quote."); }
  };

  const declineQuote = async (q) => {
    const reason = window.prompt(`Decline the quote from ${q.name}? You can add an optional reason for your records:`, "");
    if (reason === null) return;
    try {
      await api.post(`/admin/quotes/${q.id}/decline`, { reason });
      toast.success("Quote declined.");
      refreshAll();
    } catch { toast.error("Couldn't decline that quote."); }
  };

  const logout = () => { auth.clear(); nav("/admin/login"); };

  // "Needs your dates" = accepted + dates_offered, so admin can see everything that's awaiting
  const needsDates = [...accepted, ...datesOffered].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12 hs-fade" data-testid="admin-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold text-[#2C3E35] tracking-tight">Owner Dashboard</h1>
          <p className="mt-1 text-sm text-[#73777B]">Signed in as <span className="font-medium text-[#2C3E35]">{me?.email}</span></p>
        </div>
        <Button onClick={logout} variant="outline" className="rounded-full h-10 px-5" data-testid="admin-logout">
          <SignOut size={16} weight="bold" className="mr-1.5" /> Sign out
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full" data-testid="admin-tabs">
        <TabsList className="bg-[#F1ECDC] rounded-full p-1 h-auto flex flex-wrap">
          <TabsTrigger value="pending" className="rounded-full px-4 py-2 data-[state=active]:bg-[#4F6F52] data-[state=active]:text-white" data-testid="tab-pending">Pending Quotes ({pending.length})</TabsTrigger>
          <TabsTrigger value="sent" className="rounded-full px-4 py-2 data-[state=active]:bg-[#4F6F52] data-[state=active]:text-white" data-testid="tab-sent">Quote Sent ({readySent.length})</TabsTrigger>
          <TabsTrigger value="needs-dates" className="rounded-full px-4 py-2 data-[state=active]:bg-[#4F6F52] data-[state=active]:text-white" data-testid="tab-needs-dates">Offer Dates ({needsDates.length})</TabsTrigger>
          <TabsTrigger value="scheduled" className="rounded-full px-4 py-2 data-[state=active]:bg-[#4F6F52] data-[state=active]:text-white" data-testid="tab-scheduled">Booked Jobs ({booked.length})</TabsTrigger>
          <TabsTrigger value="declined" className="rounded-full px-4 py-2 data-[state=active]:bg-[#4F6F52] data-[state=active]:text-white" data-testid="tab-declined">Declined ({declined.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6 space-y-4" data-testid="pending-content">
          {pending.length === 0 && <Empty text="No pending quotes." />}
          {pending.map((q) => (
            <PendingQuoteCard key={q.id} quote={q} onSent={refreshAll} onDecline={() => declineQuote(q)} onDelete={() => deleteQuote(q)} />
          ))}
        </TabsContent>

        <TabsContent value="sent" className="mt-6 space-y-4" data-testid="sent-content">
          {readySent.length === 0 && <Empty text="No quotes awaiting client decision." />}
          {readySent.map((q) => <SummaryCard key={q.id} quote={q} onDelete={() => deleteQuote(q)} />)}
        </TabsContent>

        <TabsContent value="needs-dates" className="mt-6 space-y-4" data-testid="needs-dates-content">
          {needsDates.length === 0 && <Empty text="No accepted quotes waiting for dates." />}
          {needsDates.map((q) => <OfferDatesCard key={q.id} quote={q} onSent={refreshAll} onDelete={() => deleteQuote(q)} />)}
        </TabsContent>

        <TabsContent value="scheduled" className="mt-6 space-y-4" data-testid="scheduled-content">
          {booked.length === 0 && <Empty text="No booked jobs yet." />}
          {booked
            .slice()
            .sort((a, b) => (a.booking?.date || "").localeCompare(b.booking?.date || ""))
            .map((q) => <SummaryCard key={q.id} quote={q} showBooking onDelete={() => deleteQuote(q)} />)}
        </TabsContent>

        <TabsContent value="declined" className="mt-6 space-y-4" data-testid="declined-content">
          {declined.length === 0 && <Empty text="No declined quotes." />}
          {declined.map((q) => <DeclinedQuoteCard key={q.id} quote={q} onSent={refreshAll} onDelete={() => deleteQuote(q)} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Empty({ text }) {
  return <div className="rounded-2xl border border-dashed border-[#E5E0D8] bg-white p-10 text-center text-[#73777B]">{text}</div>;
}

function PendingQuoteCard({ quote, onSent, onDecline, onDelete }) {
  const [price, setPrice] = useState("");
  const [breakdown, setBreakdown] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const num = parseFloat(price);
    if (!num || num <= 0) { toast.error("Enter a valid quoted price."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${quote.id}/send-quote`, { price: num, breakdown, admin_notes: notes });
      toast.success("Quote sent to client.");
      onSent();
    } catch { toast.error("Couldn't send quote."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`pending-card-${quote.id}`}>
      <CardHeader quote={quote} onDelete={onDelete} />
      <CardBody quote={quote} />

      <div className="mt-6 grid sm:grid-cols-3 gap-3 items-end">
        <div>
          <Label htmlFor={`price-${quote.id}`}>Quoted price (£)</Label>
          <Input id={`price-${quote.id}`} type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid={`price-input-${quote.id}`} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor={`bd-${quote.id}`}>Breakdown (optional)</Label>
          <Input id={`bd-${quote.id}`} value={breakdown} onChange={(e) => setBreakdown(e.target.value)} placeholder="e.g. Mow £40, Hedges £60, Waste £30" className="mt-1.5" data-testid={`breakdown-input-${quote.id}`} />
        </div>
      </div>
      <div className="mt-3">
        <Label htmlFor={`n-${quote.id}`}>Notes / explanations (optional)</Label>
        <Textarea id={`n-${quote.id}`} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5" data-testid={`notes-input-${quote.id}`} />
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Button onClick={send} disabled={busy} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6" data-testid={`send-quote-${quote.id}`}>
          <PaperPlaneRight size={16} weight="fill" className="mr-1.5" /> {busy ? "Sending…" : "Send quote"}
        </Button>
        <Button onClick={onDecline} variant="outline" className="rounded-full h-11 px-5 border-[#E0B0B0] text-[#8A2C2C] hover:bg-[#FBECEC]" data-testid={`decline-quote-${quote.id}`}>
          <XCircle size={16} weight="fill" className="mr-1.5" /> Decline
        </Button>
      </div>
    </div>
  );
}

function OfferDatesCard({ quote, onSent, onDelete }) {
  const [draftDates, setDraftDates] = useState(quote.offered_dates || []);
  const [pickerDate, setPickerDate] = useState(undefined);
  const [pickerSlot, setPickerSlot] = useState("");
  const [busy, setBusy] = useState(false);

  const addDraft = () => {
    if (!pickerDate || !pickerSlot) { toast.error("Pick a date and a slot."); return; }
    const iso = toISODate(pickerDate);
    if (draftDates.find((d) => d.date === iso && d.slot === pickerSlot)) {
      toast.error("Already added."); return;
    }
    setDraftDates([...draftDates, { date: iso, slot: pickerSlot }].sort((a, b) => (a.date + a.slot).localeCompare(b.date + b.slot)));
    setPickerSlot("");
  };

  const removeDraft = (idx) => setDraftDates(draftDates.filter((_, i) => i !== idx));

  const send = async () => {
    if (draftDates.length === 0) { toast.error("Add at least one date to offer."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${quote.id}/offer-dates`, { dates: draftDates });
      toast.success(quote.status === "dates_offered" ? "Dates updated — client notified." : "Dates offered to client.");
      onSent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't offer those dates.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`offer-dates-card-${quote.id}`}>
      <CardHeader quote={quote} onDelete={onDelete} />
      <CardBody quote={quote} compact />

      {quote.price != null && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#E3EBDA] border border-[#C9D6B7] px-3 py-1 text-sm text-[#3C5A3F]">
          <CheckCircle size={14} weight="fill" /> Client accepted quote of £{Number(quote.price).toFixed(2)}
        </div>
      )}

      {quote.status === "dates_offered" && quote.offered_expires_at && (
        <ExpiryBadge expiresAt={quote.offered_expires_at} />
      )}

      {quote.specific_date_request && (
        <div className="mt-4 rounded-xl border border-[#E0D6B0] bg-[#F1ECDC] p-4" data-testid={`specific-date-request-${quote.id}`}>
          <p className="text-xs font-semibold tracking-wide uppercase text-[#7A5A12]">Client requested a specific date</p>
          {quote.specific_date_request.date && (
            <p className="mt-1 font-[Outfit] text-base font-semibold text-[#2C3E35]">{formatDateISO(quote.specific_date_request.date)}</p>
          )}
          {quote.specific_date_request.message && (
            <p className="mt-1 text-sm text-[#2C3E35] whitespace-pre-wrap">{quote.specific_date_request.message}</p>
          )}
        </div>
      )}

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div>
          <p className="text-sm font-semibold text-[#2C3E35]">Pick a date &amp; slot to offer</p>
          <p className="text-xs text-[#73777B] mt-1">Offer as many as you like, then click Send. The client picks one.</p>
          <Calendar
            mode="single"
            selected={pickerDate}
            onSelect={setPickerDate}
            disabled={(d) => d < new Date(new Date().toDateString())}
            className="mt-3 rounded-xl border border-[#E5E0D8] p-3 bg-[#F9F8F6] w-full"
            data-testid={`offer-calendar-${quote.id}`}
          />
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <Select value={pickerSlot} onValueChange={setPickerSlot}>
              <SelectTrigger className="sm:w-44" data-testid={`offer-slot-select-${quote.id}`}>
                <SelectValue placeholder="Choose slot" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="morning" data-testid={`offer-slot-morning-${quote.id}`}>Morning</SelectItem>
                <SelectItem value="afternoon" data-testid={`offer-slot-afternoon-${quote.id}`}>Afternoon</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={addDraft} className="rounded-full bg-[#2C3E35] hover:bg-black text-white h-10 px-5" data-testid={`add-offered-date-${quote.id}`}>
              <Plus size={16} weight="bold" className="mr-1" /> Add to offer
            </Button>
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-[#2C3E35]">Dates you're offering ({draftDates.length})</p>
          {draftDates.length === 0 ? (
            <p className="mt-3 text-sm text-[#73777B]">No dates added yet — pick at least one from the calendar.</p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {draftDates.map((d, i) => (
                <span key={`${d.date}-${d.slot}`} className="inline-flex items-center gap-2 rounded-full border border-[#E5E0D8] bg-[#F1ECDC] px-3 py-1.5 text-sm text-[#7A5A12]" data-testid={`draft-date-${quote.id}-${i}`}>
                  <CalendarBlank size={14} weight="fill" /> {formatDateISO(d.date)} · <span className="capitalize">{d.slot}</span>
                  <button onClick={() => removeDraft(i)} className="text-[#7A5A12]/70 hover:text-red-700" data-testid={`remove-draft-${quote.id}-${i}`}>
                    <Trash size={13} weight="bold" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <Button onClick={send} disabled={busy || draftDates.length === 0} className="mt-5 rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-11 px-6 w-full sm:w-auto" data-testid={`send-offered-dates-${quote.id}`}>
            <PaperPlaneRight size={16} weight="fill" className="mr-1.5" />
            {busy ? "Sending…" : quote.status === "dates_offered" ? "Update offered dates" : "Send dates to client"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeclinedQuoteCard({ quote, onSent, onDelete }) {
  const [showRequote, setShowRequote] = useState(false);
  const [price, setPrice] = useState(quote.price != null ? String(quote.price) : "");
  const [breakdown, setBreakdown] = useState(quote.breakdown || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async () => {
    const num = parseFloat(price);
    if (!num || num <= 0) { toast.error("Enter a valid quoted price."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/quotes/${quote.id}/send-quote`, { price: num, breakdown, admin_notes: notes });
      toast.success("Revised quote sent to client.");
      onSent();
    } catch { toast.error("Couldn't send the revised quote."); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-2xl border border-[#E5E0D8] bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`declined-card-${quote.id}`}>
      <CardHeader quote={quote} onDelete={onDelete} />
      <CardBody quote={quote} compact />

      {quote.client_message && !quote.declined_by_admin && (
        <div className="mt-4 rounded-xl border border-[#E0D6B0] bg-[#F1ECDC] p-4" data-testid={`client-query-${quote.id}`}>
          <p className="text-xs font-semibold tracking-wide uppercase text-[#7A5A12]">Client's query</p>
          <p className="mt-1 text-sm text-[#2C3E35] whitespace-pre-wrap">{quote.client_message}</p>
        </div>
      )}
      {quote.declined_by_admin && quote.admin_notes && (
        <div className="mt-4 rounded-xl border border-[#E5E0D8] bg-[#F9F8F6] p-4">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#73777B]">You declined this quote with reason:</p>
          <p className="mt-1 text-sm text-[#2C3E35] whitespace-pre-wrap">{quote.admin_notes}</p>
        </div>
      )}

      {!quote.declined_by_admin && (
        <div className="mt-5">
          {!showRequote ? (
            <Button onClick={() => setShowRequote(true)} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-10 px-5" data-testid={`open-requote-${quote.id}`}>
              <PaperPlaneRight size={16} weight="fill" className="mr-1.5" /> Send a revised quote
            </Button>
          ) : (
            <div className="space-y-3" data-testid={`requote-block-${quote.id}`}>
              <div className="grid sm:grid-cols-3 gap-3 items-end">
                <div>
                  <Label htmlFor={`rp-${quote.id}`}>Revised price (£)</Label>
                  <Input id={`rp-${quote.id}`} type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} className="mt-1.5" data-testid={`requote-price-${quote.id}`} />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor={`rb-${quote.id}`}>Updated breakdown (optional)</Label>
                  <Input id={`rb-${quote.id}`} value={breakdown} onChange={(e) => setBreakdown(e.target.value)} className="mt-1.5" data-testid={`requote-breakdown-${quote.id}`} />
                </div>
              </div>
              <div>
                <Label htmlFor={`rn-${quote.id}`}>Your reply to their query (optional)</Label>
                <Textarea id={`rn-${quote.id}`} rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Explain the changes or address their question — this goes in the client email." data-testid={`requote-notes-${quote.id}`} />
              </div>
              <div className="flex gap-2">
                <Button onClick={send} disabled={busy} className="rounded-full bg-[#4F6F52] hover:bg-[#3C5A3F] text-white h-10 px-5" data-testid={`requote-send-${quote.id}`}>
                  <PaperPlaneRight size={16} weight="fill" className="mr-1.5" /> {busy ? "Sending…" : "Re-send quote"}
                </Button>
                <Button onClick={() => setShowRequote(false)} variant="outline" className="rounded-full h-10 px-5">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ quote, highlight, showBooking, onDelete }) {
  return (
    <div className={`rounded-2xl border bg-white p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${highlight ? "border-[#86A789]" : "border-[#E5E0D8]"}`} data-testid={`summary-card-${quote.id}`}>
      <CardHeader quote={quote} onDelete={onDelete} />
      <CardBody quote={quote} compact />

      {showBooking && quote.booking && (
        <div className="mt-4 rounded-xl bg-[#E3EBDA] border border-[#C9D6B7] px-4 py-3 inline-flex items-center gap-2 text-sm text-[#2C3E35]">
          <CalendarBlank size={16} weight="fill" /> <span className="font-semibold">{formatDateISO(quote.booking.date)}</span> · <span className="capitalize">{quote.booking.slot}</span>
        </div>
      )}

      {quote.photos?.length > 0 && (
        <div className="mt-4 grid grid-cols-4 sm:grid-cols-6 gap-2">
          {quote.photos.map((p) => (
            <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block aspect-square rounded-lg overflow-hidden border border-[#E5E0D8]">
              <img src={photoUrl(p.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function CardHeader({ quote, onDelete }) {
  return (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h3 className="font-[Outfit] text-xl font-semibold text-[#2C3E35]">{quote.name}</h3>
        <p className="text-sm text-[#73777B] mt-0.5">{quote.email} · {quote.phone}</p>
        <p className="text-sm text-[#73777B]">{quote.address}</p>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={quote.status} />
        {onDelete && (
          <button
            onClick={onDelete}
            title="Delete this quote"
            className="p-2 rounded-full text-[#73777B] hover:text-red-700 hover:bg-red-50 transition-colors"
            data-testid={`delete-quote-${quote.id}`}
          >
            <Trash size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function CardBody({ quote, compact = false }) {
  return (
    <>
      <div className={`mt-4 grid ${compact ? "sm:grid-cols-3" : "sm:grid-cols-3"} gap-4 text-sm`}>
        <Field label="Frequency" value={FREQUENCY_LABEL[quote.frequency]} />
        <Field label="Waste" value={WASTE_LABEL[quote.waste_disposal]} />
        {quote.price != null ? (
          <Field label="Quoted" value={`£${Number(quote.price).toFixed(2)}`} />
        ) : (
          <Field label="Submitted" value={new Date(quote.created_at).toLocaleString()} />
        )}
      </div>
      <div className="mt-3"><Field label="Visit preference" value={VISIT_PREFERENCE_LABEL[quote.visit_preference]} /></div>
      <div className="mt-3"><Field label="Services" value={SERVICES.filter((s) => quote.services?.includes(s.id)).map((s) => s.label).join(", ") + (quote.other_service ? ` — ${quote.other_service}` : "")} /></div>
      {!compact && quote.notes && <div className="mt-3"><Field label="Client notes" value={quote.notes} /></div>}
      {!compact && quote.photos?.length > 0 && (
        <div className="mt-5">
          <p className="text-xs font-semibold tracking-wide uppercase text-[#73777B] mb-2">Photos ({quote.photos.length})</p>
          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
            {quote.photos.map((p) => (
              <a key={p.id} href={photoUrl(p.id)} target="_blank" rel="noreferrer" className="block aspect-square rounded-xl overflow-hidden border border-[#E5E0D8]">
                <img src={photoUrl(p.id)} alt="" className="w-full h-full object-cover" loading="lazy" />
              </a>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold tracking-wide uppercase text-[#73777B]">{label}</p>
      <p className="mt-0.5 text-sm text-[#2C3E35]">{value || "—"}</p>
    </div>
  );
}

function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatDateISO(iso) {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function ExpiryBadge({ expiresAt }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const diffMs = new Date(expiresAt).getTime() - now;
  const mins = Math.round(Math.abs(diffMs) / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  const human = hrs > 0 ? `${hrs}h ${rem}m` : `${rem}m`;
  const expired = diffMs <= 0;
  const cls = expired
    ? "bg-[#F1DCDC] text-[#8A2C2C] border-[#E0B0B0]"
    : diffMs < 30 * 60 * 1000
      ? "bg-[#F1ECDC] text-[#7A5A12] border-[#E0D6B0]"
      : "bg-[#E0E9F0] text-[#2B4A66] border-[#BDD0DD]";
  return (
    <div className={`mt-3 inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${cls}`} data-testid="expiry-badge">
      {expired ? `Hold expired ${human} ago — releasing soon` : `Hold expires in ${human}`}
    </div>
  );
}
